/** Quick Readiness Snapshot - public 6-question flow that shows an initial
 * "Snapshot" result BEFORE requiring signup. This is the primary hero CTA.
 * Full 30-question diagnostic lives behind free signup.
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { trackEvent } from "@/lib/analytics";
import { isFeatureOn, usePublicSettings } from "@/lib/siteSettings";
import { useAuth } from "@/context/AuthContext";
import LockPanel from "@/components/LockPanel";
import {
  ArrowRight, ArrowLeft, CheckCircle2, XCircle, Loader2, Sparkles,
  Target, Award, AlertCircle, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ADC blueprint weights — used to break ties between equally-weak subjects and
// to pick the highest-weight "Not tested yet" subject.
const SUBJECT_WEIGHTS = {
  oral_surgery: 0.15, prosthodontics: 0.12, endodontics: 0.10, operative: 0.10,
  periodontics: 0.12, orthodontics: 0.10, oral_medicine: 0.12, pharmacology: 0.08,
  pediatric: 0.07, community: 0.04,
};

const SUBJECT_LABEL = {
  oral_surgery: "Oral Surgery",
  prosthodontics: "Prosthodontics",
  oral_medicine: "Oral Medicine & Pathology",
  periodontics: "Periodontics",
  endodontics: "Endodontics",
  orthodontics: "Orthodontics",
  pharmacology: "Pharmacology",
  operative: "Operative Dentistry",
  pediatric: "Pediatric Dentistry",
  community: "Community Dentistry",
};

const seoTitle = "Free ADC Readiness Check - 3-Minute Snapshot | PassADC";
const seoDesc = "Take our free 3-minute readiness check for the ADC Part 1 written exam. See your initial subject strengths and weak areas - no credit card, no signup required to get your snapshot.";

function useSeo() {
  useEffect(() => {
    const prev = document.title;
    document.title = seoTitle;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    if (desc) desc.setAttribute("content", seoDesc);
    return () => {
      document.title = prev;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, []);
}

const IntroScreen = ({ onStart }) => (
  <div className="max-w-xl mx-auto text-center" data-testid="quick-check-intro">
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-6">
      <Sparkles className="w-3.5 h-3.5 text-primary" />
      <span className="text-xs font-medium text-primary">Free · No signup to get your result</span>
    </div>
    <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-5">
      Find out where you stand.
    </h1>
    <p className="text-base md:text-lg text-muted-foreground mb-8">
      Answer 6 ADC-style questions and we&apos;ll identify where you may need to focus your preparation.
    </p>
    <Button
      size="lg"
      className="rounded-full px-8 py-6 text-lg font-semibold glow-primary"
      onClick={onStart}
      data-testid="quick-check-start-btn"
    >
      Start My Free Check
      <ArrowRight className="ml-2 w-5 h-5" />
    </Button>
    <p className="text-xs text-muted-foreground mt-4">
      Takes about 3 minutes · No credit card
    </p>
  </div>
);

const QuestionScreen = ({ questions, index, onAnswer, onBack }) => {
  const q = questions[index];
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(null); // { correct_option_id, is_correct, explanation }
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setSelected(null); setRevealed(null); }, [index]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/diagnostic/check`, {
        question_id: q.id, selected_option_id: selected,
      });
      setRevealed(data);
      if (index === 0) trackEvent("quick_check_question_1", { subject: q.subject_id });
    } catch {
      setRevealed({ correct_option_id: null, is_correct: false });
    } finally { setSubmitting(false); }
  };

  const next = () => {
    onAnswer({
      question_id: q.id,
      subject_id: q.subject_id,
      selected_option_id: selected,
      is_correct: !!revealed?.is_correct,
    });
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="quick-check-question">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-xs text-muted-foreground">
          Question {index + 1} of {questions.length}
        </span>
      </div>
      <div className="w-full bg-secondary rounded-full h-1.5 mb-8">
        <div
          className="bg-primary h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${((index + (revealed ? 1 : 0)) / questions.length) * 100}%` }}
        />
      </div>

      <p className="text-xs uppercase tracking-wider text-primary mb-3">
        {SUBJECT_LABEL[q.subject_id] || q.subject_id}
      </p>
      <h2 className="text-lg md:text-xl font-semibold leading-snug mb-6">{q.question_text}</h2>

      <div className="space-y-3 mb-6">
        {q.options.map((opt) => {
          const isSel = selected === opt.id;
          const isCorrect = revealed && revealed.correct_option_id === opt.id;
          const isWrongSel = revealed && isSel && !isCorrect;
          return (
            <button
              key={opt.id}
              onClick={() => !revealed && setSelected(opt.id)}
              disabled={!!revealed}
              data-testid={`quick-check-option-${opt.id}`}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                isCorrect ? "border-success bg-success/10"
                : isWrongSel ? "border-destructive bg-destructive/10"
                : isSel ? "border-primary bg-primary/10"
                : "border-border hover:border-primary/40 bg-card"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  isCorrect ? "bg-success text-white"
                  : isWrongSel ? "bg-destructive text-white"
                  : isSel ? "bg-primary text-white" : "border border-border"
                }`}>
                  {isCorrect ? <CheckCircle2 className="w-4 h-4" /> : isWrongSel ? <XCircle className="w-4 h-4" /> : null}
                </div>
                <span className="text-sm">{opt.text}</span>
              </div>
            </button>
          );
        })}
      </div>

      {!revealed ? (
        <Button
          className="w-full rounded-full py-6 text-base font-semibold"
          onClick={submit}
          disabled={!selected || submitting}
          data-testid="quick-check-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check answer"}
        </Button>
      ) : (
        <Button
          className="w-full rounded-full py-6 text-base font-semibold glow-primary"
          onClick={next}
          data-testid="quick-check-next-btn"
        >
          {index + 1 < questions.length ? "Next question" : "See my snapshot"}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

const SnapshotScreen = ({ answers }) => {
  const navigate = useNavigate();
  const { user, isPro } = useAuth();
  const settings = usePublicSettings();
  const freemiumV2 = settings["features.freemium_v2"] === true;
  const pwCopy = settings["paywall_copy"] || {};
  const [planEnabled, setPlanEnabled] = useState(false);
  const [fixLock, setFixLock] = useState(null); // subject label when the results_fix wall is shown
  const postedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    isFeatureOn("ten_minute_plan").then((on) => { if (mounted) setPlanEnabled(on); });
    return () => { mounted = false; };
  }, []);

  // Compute snapshot client-side
  const subjectBreakdown = {};
  answers.forEach((a) => {
    if (!subjectBreakdown[a.subject_id]) subjectBreakdown[a.subject_id] = { correct: 0, total: 0 };
    subjectBreakdown[a.subject_id].total += 1;
    if (a.is_correct) subjectBreakdown[a.subject_id].correct += 1;
  });
  const correctCount = answers.filter((a) => a.is_correct).length;
  const total = answers.length || 1;
  const pct = Math.round((correctCount / total) * 100);
  const questionCount = answers.length;

  // Level buckets
  let level = "Emerging";
  let levelDesc = "Early stage - a structured plan will make the biggest difference here.";
  let levelColor = "text-warning";
  if (pct >= 66) {
    level = "Ready-ish";
    levelDesc = "Solid base. Refine the weak areas and rehearse full timed mocks.";
    levelColor = "text-success";
  } else if (pct >= 34) {
    level = "Developing";
    levelDesc = "Foundations are forming. Targeted practice will lift you quickly.";
    levelColor = "text-primary";
  }

  // Strongest + focus (deterministic when tied)
  const sorted = Object.entries(subjectBreakdown).map(([sid, v]) => ({
    sid, ratio: v.correct / (v.total || 1),
  })).sort((a, b) => b.ratio - a.ratio || a.sid.localeCompare(b.sid));

  const strongest = sorted[0];
  const focus = [...sorted].sort((a, b) => a.ratio - b.ratio || a.sid.localeCompare(b.sid))[0];

  // Two weakest subjects: lowest % correct; tie -> higher ADC blueprint weight.
  // If fewer than 2 subjects were answered, pad with the highest-weight untested one.
  const weightOf = (sid) => SUBJECT_WEIGHTS[sid] || 0;
  const answeredAsc = Object.entries(subjectBreakdown).map(([sid, v]) => ({
    sid, correct: v.correct, total: v.total, ratio: v.correct / (v.total || 1),
  })).sort((a, b) => a.ratio - b.ratio || weightOf(b.sid) - weightOf(a.sid) || a.sid.localeCompare(b.sid));
  const weakCards = answeredAsc.slice(0, 2);
  if (weakCards.length < 2) {
    const tested = new Set(answeredAsc.map((c) => c.sid));
    const untested = Object.keys(SUBJECT_WEIGHTS)
      .filter((id) => !tested.has(id))
      .sort((a, b) => weightOf(b) - weightOf(a) || a.localeCompare(b));
    if (untested[0]) weakCards.push({ sid: untested[0], notTested: true });
  }
  const planSubject = answeredAsc[0]?.sid;

  const goToPlan = () => {
    trackEvent("plan_cta_clicked", { subject_id: planSubject, logged_in: !!user });
    if (user) { navigate(`/plan/today?subject=${planSubject}`); return; }
    try { localStorage.setItem("passadc_plan_subject", planSubject); } catch { /* ignore */ }
    trackEvent("signup_started", { source: "quick_check_plan_cta" });
    navigate(`/signup?from=quick_check&plan_subject=${planSubject}`);
  };

  useEffect(() => {
    if (postedRef.current) return;
    postedRef.current = true;
    // Persist the snapshot so it can be carried into the account at signup
    // (survives the Google OAuth round trip since it's same-origin localStorage).
    try {
      localStorage.setItem("passadc_snapshot", JSON.stringify({
        scores: subjectBreakdown, level, score_pct: pct, at: Date.now(),
      }));
    } catch { /* ignore */ }
    // Server-side funnel log (STEP 21 tracking) — fired once per snapshot.
    axios.post(`${API}/readiness/complete`, { score_pct: pct, level }).catch(() => {});
    trackEvent("quick_check_completed", {
      score_pct: pct, level,
      strongest: strongest?.sid,
      focus: focus?.sid,
    });
    trackEvent("readiness_result_viewed", { level, score_pct: pct });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-2xl mx-auto" data-testid="quick-check-snapshot">
      {fixLock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setFixLock(null)} data-testid="results-fix-overlay">
          <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <LockPanel
              wall="results_fix"
              title={(pwCopy.results_fix_title || "Your weakest area is {subject}").replace("{subject}", fixLock)}
              body={pwCopy.results_fix_body || "Pro unlocks the lessons and retests for it."}
              cta={pwCopy.results_fix_cta || "Unlock with Pro"}
              secondaryLabel="Not now"
              onSecondary={() => setFixLock(null)}
            />
          </div>
        </div>
      )}
      <div className="text-center mb-2">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Your ADC Readiness Snapshot</p>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-center mb-3">
        You&apos;re at the <span className={levelColor}>{level}</span> stage.
      </h1>
      <p className="text-center text-muted-foreground text-sm md:text-base max-w-lg mx-auto mb-10">
        {levelDesc}
      </p>

      {planEnabled && (
        <div className="mb-10" data-testid="plan-results-block">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-5" data-testid="snapshot-label">
            <Clock className="w-3.5 h-3.5" />
            <span>Quick snapshot · {questionCount} questions · about 3 minutes</span>
          </div>
          <h2 className="text-center text-lg md:text-xl font-bold mb-4">Your 2 weakest subjects</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-xl mx-auto">
            {weakCards.map((c) => (
              <div key={c.sid} className="rounded-2xl border border-warning/30 bg-warning/5 p-5" data-testid={`weak-card-${c.sid}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-warning" />
                  <p className="font-semibold">{SUBJECT_LABEL[c.sid] || c.sid}</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {c.notTested ? "Not tested yet" : `${c.correct} of ${c.total} correct`}
                </p>
                {user && (
                  <button
                    data-testid={`fix-this-${c.sid}`}
                    onClick={() => {
                      if (freemiumV2 && !isPro) { setFixLock(SUBJECT_LABEL[c.sid] || c.sid); return; }
                      navigate(`/learn/${c.sid}`);
                    }}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  >
                    Fix this <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <Button
            size="lg"
            className="w-full max-w-xl mx-auto flex rounded-full py-6 text-base font-semibold glow-primary"
            onClick={goToPlan}
            data-testid="start-plan-btn"
          >
            Start your 10-minute plan
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="glass rounded-2xl border border-border p-5 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Current level</p>
          <div className="flex items-center justify-center gap-2">
            <AlertCircle className={`w-5 h-5 ${levelColor}`} />
            <p className={`text-lg font-bold ${levelColor}`}>{level}</p>
          </div>
        </div>
        <div className="glass rounded-2xl border border-border p-5 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Strongest area</p>
          <div className="flex items-center justify-center gap-2">
            <Award className="w-5 h-5 text-success" />
            <p className="text-base font-semibold">{SUBJECT_LABEL[strongest?.sid] || "-"}</p>
          </div>
        </div>
        <div className="glass rounded-2xl border border-border p-5 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Area to focus on</p>
          <div className="flex items-center justify-center gap-2">
            <Target className="w-5 h-5 text-warning" />
            <p className="text-base font-semibold">{SUBJECT_LABEL[focus?.sid] || "-"}</p>
          </div>
        </div>
      </div>

      {/* Personalised 7-day plan preview — the "next steps" the audit called for */}
      <div className="rounded-2xl border border-border bg-card p-5 md:p-6 mb-6" data-testid="quick-check-plan-preview">
        <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Your next 7 days</div>
        <h3 className="text-lg font-bold mb-3">Based on this snapshot, here&apos;s where the highest-return hours are.</h3>
        <ol className="space-y-2.5 text-sm">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">1</span>
            <span><strong>Days 1–2:</strong> 20 targeted MCQs in <strong>{SUBJECT_LABEL[focus?.sid] || "your weakest subject"}</strong> with worked explanations after each.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">2</span>
            <span><strong>Days 3–4:</strong> Sit the full 30-question Readiness Trial to establish a real baseline across all 10 subjects.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">3</span>
            <span><strong>Days 5–6:</strong> Focused revision of every question you got wrong, using the built-in tutor for the ones you couldn&apos;t explain back.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">4</span>
            <span><strong>Day 7:</strong> Retake a shorter drill in your weak subject and watch your accuracy on it climb. That&apos;s the loop that lifts your Readiness Score.</span>
          </li>
        </ol>
      </div>

      {/* Sample worked explanation — proof of teaching quality, one of the audit's top recommendations */}
      <div className="rounded-2xl border border-border bg-card p-5 md:p-6 mb-8" data-testid="quick-check-sample-explanation">
        <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Sample worked explanation</div>
        <h3 className="text-base font-bold mb-3">This is what you see after every question inside PassADC.</h3>
        <div className="text-sm bg-muted/40 rounded-lg p-4 mb-3">
          <p className="font-medium mb-2">Q. A 24-year-old presents 45 minutes after avulsion of tooth 11. The tooth has been kept in milk. Which of the following is the most appropriate immediate management?</p>
          <p className="text-muted-foreground mb-1"><strong>Correct:</strong> Rinse the tooth briefly in saline, replant, and splint for 2 weeks.</p>
          <p className="text-muted-foreground"><strong>Why:</strong> With extra-oral time under 60 min and the tooth stored in a physiologic medium (milk keeps periodontal ligament cells viable), immediate replantation gives the best long-term prognosis. A flexible splint for 2 weeks (per current Australian trauma guidelines) allows periodontal healing without inducing ankylosis.</p>
        </div>
        <p className="text-xs text-muted-foreground">Every explanation is mapped to current Australian guidelines. Not just an answer key. A reason.</p>
      </div>

      <div className="rounded-2xl border border-primary/25 bg-primary/5 p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-bold mb-2 text-center">Unlock your full plan</h2>
        <p className="text-sm text-muted-foreground text-center mb-6">Free account gives you:</p>
        <ul className="space-y-2 text-sm max-w-md mx-auto mb-6">
          {[
            "Subject-by-subject breakdown across all 10 subjects",
            "Your recommended study priorities",
            "Start practising your weak areas today",
            "The full 30-question readiness assessment",
            "Track your readiness score over time",
            "7 days of full Pro included, no card",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Button
          size="lg"
          variant={planEnabled ? "outline" : "default"}
          className="w-full rounded-full py-6 text-base font-semibold"
          onClick={() => {
            trackEvent("signup_started", { source: "quick_check_snapshot" });
            navigate("/signup?from=quick_check");
          }}
          data-testid="quick-check-signup-btn"
        >
          Unlock My Full Plan
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Free · No credit card · Takes 30 seconds
        </p>
      </div>

      <p className="text-xs text-muted-foreground text-center mt-8 max-w-md mx-auto">
        This snapshot is based on 6 sample questions - it&apos;s an initial indicator, not a
        prediction of your official ADC result. Your full readiness score updates as you
        study inside PassADC.
      </p>

      <div className="text-center mt-6">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4">
          Back to home
        </Link>
      </div>
    </div>
  );
};

const QuickReadinessPage = () => {
  useSeo();
  const [stage, setStage] = useState("intro"); // intro | questions | snapshot
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    trackEvent("readiness_cta_clicked", { page: "quick_check_landed" });
  }, []);

  const start = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/questions/quick-check`);
      if (!data.questions || data.questions.length === 0) {
        setError("No questions available right now. Please try again shortly.");
        return;
      }
      setQuestions(data.questions);
      setIndex(0);
      setAnswers([]);
      setStage("questions");
      trackEvent("quick_check_started", { question_count: data.questions.length });
    } catch {
      setError("Could not load questions. Please try again shortly.");
    } finally { setLoading(false); }
  };

  const recordAnswer = (a) => {
    const next = [...answers, a];
    setAnswers(next);
    if (index + 1 < questions.length) setIndex(index + 1);
    else setStage("snapshot");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-16 md:py-24" data-testid="quick-readiness-page">
        {stage === "intro" && (
          <>
            <IntroScreen onStart={start} />
            {loading && (
              <div className="text-center mt-6 text-sm text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading questions…
              </div>
            )}
            {error && <p className="text-center mt-6 text-sm text-destructive">{error}</p>}
          </>
        )}
        {stage === "questions" && (
          <QuestionScreen
            questions={questions}
            index={index}
            onAnswer={recordAnswer}
            onBack={() => (index > 0 ? setIndex(index - 1) : setStage("intro"))}
          />
        )}
        {stage === "snapshot" && <SnapshotScreen answers={answers} />}
      </div>
      <Footer />
    </div>
  );
};

export default QuickReadinessPage;
